import { FOLDER_UPLOAD_TYPE } from '@unifyapps/defs/blocks/FileUpload/contentSchema';
import type { TFunction } from '@unifyapps/i18n/client';
import type { SnackbarProps } from '@unifyapps/ui/components/Snackbar/types';
import { isFileTypeAllowed } from '../../../../../../components/FileInput/utils/isFileTypeAllowed';

export interface FileValidationConfig {
  maxFiles?: number;
  maxFileSize?: number; // in MB
  multipleFiles?: boolean;
  supportedFileTypes?: string[];
}

export type FileValidationResult =
  | {
      isValid: true;
      error?: never;
    }
  | {
      isValid: false;
      error: string;
    };

export function validateFileCount(
  fileCount: number,
  config: Pick<FileValidationConfig, 'maxFiles'>,
): FileValidationResult {
  if (config.maxFiles && fileCount > config.maxFiles) {
    return {
      isValid: false,
      error: `Too many files selected. Maximum allowed: ${config.maxFiles}`,
    };
  }
  return { isValid: true };
}

export function validateFileSize(
  fileSize: number,
  fileName: string,
  config: Pick<FileValidationConfig, 'maxFileSize'>,
): FileValidationResult {
  if (config.maxFileSize && fileSize > config.maxFileSize * 1024 * 1024) {
    return {
      isValid: false,
      error: `File ${fileName} exceeds maximum size limit: ${config.maxFileSize} MB`,
    };
  }
  return { isValid: true };
}

export function validateAndFilterFiles<T extends { size?: number; name: string }>(
  files: T[],
  config: FileValidationConfig,
): { validFiles: T[]; errors: string[] } {
  const errors: string[] = [];

  // Check max files limit
  const countValidation = validateFileCount(files.length, config);
  if (!countValidation.isValid) {
    errors.push(countValidation.error);
    return { validFiles: [], errors };
  }

  // Filter files by size
  const validFiles = files.filter((file) => {
    const sizeValidation = validateFileSize(file.size || 0, file.name, config);
    if (!sizeValidation.isValid) {
      errors.push(sizeValidation.error);
      return false;
    }
    return true;
  });

  return { validFiles, errors };
}

export function createFileMeta(blockId: string, referenceId: string, instanceId?: string) {
  return { entityId: blockId, referenceId: `${referenceId}-${blockId}`, instanceId };
}

// Expand JPG support to include JPEG, as .jpg files are identified as JPEG format by some systems
function expandJpgAliases(fileTypes: string[]): string[] {
  if (fileTypes.includes('image/jpg') && !fileTypes.includes('image/jpeg')) {
    return [...fileTypes, 'image/jpeg'];
  }

  return fileTypes;
}

/**
 * Validates a list of files against constraints:
 *  - Maximum number of files (`maxFiles`)
 *  - Maximum file size in MB (`maxFileSize`)
 *  - Supported file types (`supportedFileTypes`)
 *
 * Files exceeding these limits are trimmed or filtered out,
 * and corresponding alerts are shown.
 *
 * @returns The list of valid files after applying constraints
 */
export function validateAndFilterMediaUploads<
  T extends { name: string; type?: string; mimeType?: string },
>({
  files,
  maxFileSize,
  maxFiles,
  supportedFileTypes,
  getFileSize,
  t,
  showSnackbar,
}: {
  files: T[];
  maxFileSize: number;
  maxFiles?: number;
  supportedFileTypes?: string[];
  getFileSize: (file: T) => number | undefined;
  t: TFunction;
  showSnackbar: (props: SnackbarProps) => void;
}) {
  let validFiles = [...files];

  // Check max files limit
  if (maxFiles && validFiles.length > maxFiles) {
    showSnackbar({
      color: 'error',
      title: t('common:Uppy.MaxNumberOfFiles', { max: maxFiles }),
    });
    validFiles = validFiles.slice(0, maxFiles);
  }

  // Filter files by file type (excluding folder type which is a special flag, not an actual mime type)
  const fileTypesForValidation = supportedFileTypes?.filter((type) => type !== FOLDER_UPLOAD_TYPE);
  const expandedFileTypes = fileTypesForValidation
    ? expandJpgAliases(fileTypesForValidation)
    : fileTypesForValidation;
  const accept = expandedFileTypes?.join(',');
  let hasInvalidFileType = false;

  if (accept && accept !== '*' && accept !== '*/*') {
    validFiles = validFiles.filter((file) => {
      // create a File-like object for isFileTypeAllowed
      const fileObj = {
        name: file.name,
        type: file.type ?? file.mimeType ?? '',
      } as File;

      if (!isFileTypeAllowed(accept, fileObj)) {
        hasInvalidFileType = true;
        return false;
      }
      return true;
    });
  }

  if (hasInvalidFileType) {
    showSnackbar({
      color: 'error',
      title: t('common:Uppy.FileTypeNotAllowed', { accept }),
    });
  }

  // Filter files by size
  let hasOversizedFiles = false;

  validFiles = validFiles.filter((file) => {
    const fileSize = getFileSize(file);
    if (fileSize && fileSize > maxFileSize * 1024 * 1024) {
      hasOversizedFiles = true;
      return false;
    }
    return true;
  });

  if (hasOversizedFiles) {
    showSnackbar({
      color: 'error',
      title: t('common:Uppy.FileSizeExceeds', { size: maxFileSize }),
    });
  }

  return validFiles;
}
